/**
 * @fileoverview Shared right-side drawer shell (LumiarDrawer) used by the
 * submission drawers: fixed header (title + optional badge + close), a
 * scrollable body and an optional FIXED footer below the scroll area (so the
 * submit action never floats over / leaks behind the content).
 */

import { ReactNode } from 'react'
import { ActionIcon, Box, Group, Title } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'
import { DRAWER_WIDTH } from '../constants'

interface StudentTaskDrawerShellProps {
  opened: boolean
  title: string
  onClose: () => void
  badge?: ReactNode
  /** Pinned below the scroll area (e.g. the submit button); hidden when absent. */
  footer?: ReactNode
  children: ReactNode
}

export function StudentTaskDrawerShell({
  opened,
  title,
  onClose,
  badge,
  footer,
  children,
}: StudentTaskDrawerShellProps) {
  return (
    <LumiarDrawer
      visibility={opened}
      onChange={(visible: boolean) => !visible && onClose()}
      side="right"
      scrollable={false}
    >
      <Box
        style={{
          width: DRAWER_WIDTH,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Group position="apart" align="center" px="lg" py="md" noWrap>
          <Group spacing="xs" align="center" noWrap>
            <Title order={3}>{title}</Title>
            {badge}
          </Group>
          <ActionIcon variant="subtle" onClick={onClose} aria-label="Close">
            <IconX size={20} />
          </ActionIcon>
        </Group>

        <Box px="lg" pb="lg" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {children}
        </Box>

        {footer && (
          <Group
            position="right"
            px="lg"
            py="sm"
            sx={theme => ({
              borderTop: `1px solid ${theme.colors.gray[2]}`,
              background: theme.white,
              flexShrink: 0,
            })}
          >
            {footer}
          </Group>
        )}
      </Box>
    </LumiarDrawer>
  )
}
